/* eslint-disable react-refresh/only-export-components */
/*
 * useRohdatenPanel.tsx - Rohdaten zu einem Entity anzeigen.
 *
 * Created by Dr. Maximillian Dornseif 2021-09
 * Copyright (c) 2021, 2023 HUDORA GmbH
 */

import { useQuery } from '@apollo/client'
import { LadeChecker } from '@hudora/hd-react-components'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { assertIsString } from 'assertate-debug'
import gql from 'graphql-tag'
import React, { ReactElement, useCallback, useState } from 'react'
import ReactJson from 'react-json-view'

import { IEntity } from '../../types'
import { useHdPanel } from '../../WaWi/components/HdPanel'
import { HdRohdatenIcon } from '../components/HdIcons'
import { ICommandBarItemProps } from '../muipanels'
import { TusePanelReturn } from './hookTypes'

export interface IRohdatenPanel3Options {
  /** The Entity which should be displayed
   *
   */
  entity: IEntity
  /** Name des Daten-Schemas, dass angezeigt wird.
   *
   */
  schemaName: string
  /** Title of the Dialog/Panel. Defaults to 'Rohdaten'.
   *
   */
  title?: string
}

export function useRohdatenPanel3(options: IRohdatenPanel3Options): TusePanelReturn {
  assertIsString(options.schemaName)
  const { openDialog, renderDialog } = useHdPanel(
    options.title || 'Rohdaten',
    'Die Daten, wie sie unserem System zugrunde liegen und in der Datenbank gespeichert sind.'
  )
  const [entity, setEntity] = useState<IEntity>(options?.entity)

  const myOpenDialog = useCallback(
    (e?: IEntity): void => {
      if (e) {
        setEntity(e)
      }
      openDialog()
    },
    [openDialog]
  )

  const renderOpenButton = useCallback(
    (e?: IEntity): ReactElement => (
      <IconButton size="small" color="primary" title="Rohdaten" onClick={() => myOpenDialog(e)}>
        <HdRohdatenIcon />
      </IconButton>
    ),
    [openDialog]
  )

  const commandBarItem: ICommandBarItemProps = {
    key: 'rohdaten',
    text: 'Datensatz-Rohdaten',
    ariaLabel: 'Rohdaten',
    iconOnly: true,
    iconProps: { iconName: 'PageData' },
    onClick: () => openDialog(),
  }

  const dialog = renderDialog({
    renderBody: () => (
      <Box>
        {entity || options?.entity ? (
          <ReactJson
            src={entity || options?.entity}
            name={options.title || 'Rohdaten'}
            collapsed={1}
            collapseStringsAfterLength={30}
            displayDataTypes={false}
            iconStyle="square"
            theme="monokai"
          />
        ) : (
          <span>Keine Daten</span>
        )}

        {entity || options?.entity ? (
          <FieldScanner schemaName={options?.schemaName} entity={entity || options?.entity} />
        ) : null}

        {(entity || options?.entity)?.id ? <EntityDumpRaw id={(entity || options?.entity)?.id} /> : null}
      </Box>
    ),
  })

  return [commandBarItem, dialog, myOpenDialog, renderOpenButton]
}

const INTROSPECTION_QUERY = gql`
  query IntrospectionQuery($name: String!) {
    __type(name: $name) {
      name
      fields {
        name
        type {
          name
          kind
          ofType {
            name
            kind
          }
        }
      }
    }
  }
`

const FieldScanner = (props: { schemaName: string; entity: any }) => {
  const { loading, error, data } = useQuery(INTROSPECTION_QUERY, {
    variables: { name: props.schemaName },
    skip: !props.schemaName,
  })
  if (!data) {
    return <LadeChecker loading={loading} error={error} label="Introspection" />
  }
  const missing: string[] = []
  if (data?.__type?.fields) {
    for (const field of data?.__type?.fields || []) {
      if (props.entity?.[field.name] === undefined) {
        missing.push(field.name)
      }
    }
  }

  if (missing.length < 1) {
    return null
  }

  return (
    <Box sx={{ pt: 4 }}>
      <LadeChecker loading={loading} error={error} label="Introspection" />
      <Typography variant="h4" component="h3">
        Nicht abgefragte Felder
      </Typography>
      <div
        style={{
          fontFamily: 'monospace',
          cursor: 'default',
          backgroundColor: 'rgb(39, 40, 34)',
        }}
      >
        <pre
          style={{
            color: 'rgb(249, 248, 245)',
            letterSpacing: '0.5px',
            verticalAlign: 'top',
            opacity: '0.85',
          }}
        >
          {missing.join('\n')}
        </pre>
      </div>
    </Box>
  )
}

const RAW_QUERY = gql`
  query RawQuery($id: ID!) {
    rawDatastoreEntity(id: $id)
  }
`

const EntityDumpRaw = (props: { id: string }) => {
  const { loading, error, data } = useQuery(RAW_QUERY, {
    variables: { id: props.id },
  })
  if (!data) {
    return <LadeChecker loading={loading} error={error} label="Lade Rohdaten" />
  }
  return (
    <Box sx={{ pt: 4 }}>
      <LadeChecker loading={loading} error={error} label="Lade Rohdaten" />
      <Typography variant="h4" component="h3">
        Datastore-Entity
      </Typography>
      <ReactJson
        src={JSON.parse(data?.rawDatastoreEntity)}
        name="Datastore"
        collapsed={0}
        collapseStringsAfterLength={30}
        displayDataTypes={false}
        iconStyle="square"
        theme="monokai"
      />
    </Box>
  )
}
